import React from "react";
import {
    Card,
    CardBody,
    Container,
    Row,
    Col,
    Pagination,
    PaginationItem,
    PaginationLink,
    Input,
    Button,
    InputGroup,
    Form,
    Alert
} from "reactstrap";
import { useNavigate, Navigate } from "react-router-dom";
import { sessionEnded } from "../user/SessionEnded";
import Api from "../../components/helpers/Api";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";

class AdminThemes extends React.Component {
    state = {
        isLoading: false,
        themes: [],
        totalPages: 0,
        currentPage: 0,
        searchQuery: "",
        unauthorized: false,
        createTitle: "",
        creating: false,
        createSuccess: false,
        createError: null
    };

    componentDidMount() {
        try {
            const customer = sessionStorage.getItem("customer");
            const parsed = customer ? JSON.parse(customer) : null;
            console.log("AdminThemes customer structure:", parsed);
            if (parsed?.user?.isAdmin) {
                this.loadPage(0);
            } else {
                this.setState({ unauthorized: true });
            }
        } catch (e) {
            console.error("AdminThemes componentDidMount error:", e);
            this.setState({ unauthorized: true });
        }
    }

    loadPage = (pageNumber) => {
        const { searchQuery } = this.state;
        const params = new URLSearchParams({ page: pageNumber, size: 10 });
        if (searchQuery.trim()) params.append("query", searchQuery.trim());

        this.setState({ isLoading: true });
        Api.get(`/themes/paginated?${params.toString()}`)
            .then(({ data }) => {
                this.setState({
                    themes: data.content,
                    totalPages: data.totalPages,
                    currentPage: pageNumber,
                    isLoading: false
                });
            })
            .catch((err) => {
                console.error("Error fetching themes:", err);
                this.setState({ isLoading: false });
            });
    };

    handleSearchChange = (e) => this.setState({ searchQuery: e.target.value });
    handleSearchSubmit = (e) => { e.preventDefault(); this.loadPage(0); };
    handleClearSearch = () => this.setState({ searchQuery: "" }, () => this.loadPage(0));
    handleCreateChange = (e) => this.setState({ createTitle: e.target.value });
    handleCreateSubmit = () => {
        const title = this.state.createTitle.trim();
        if (!title) return;

        this.setState({ creating: true, createError: null, createSuccess: false });
        Api.post("/themes", { title })
            .then(() => {
                this.setState({ createTitle: "", createSuccess: true });
                this.loadPage(0);
                setTimeout(() => this.setState({ createSuccess: false }), 3000);
            })
            .catch(err => {
                this.setState({
                    createError: err.response?.data?.error || "Failed to create theme. Please try again."
                });
            })
            .finally(() => this.setState({ creating: false }));
    };

    renderPagination() {
        const { currentPage, totalPages } = this.state;
        if (totalPages <= 1) return null;

        const pages = [];
        const max=5;
        let start = Math.max(0, currentPage - Math.floor(max/2));
        let end = Math.min(totalPages-1, start+max-1);
        if (end - start < max-1) start = Math.max(0, end - (max-1));

        for (let i = start; i <= end; i++) {
            pages.push(
                <PaginationItem active={i===currentPage} key={i}>
                    <PaginationLink onClick={()=>this.loadPage(i)}>{i+1}</PaginationLink>
                </PaginationItem>
            );
        }

        return (
            <Pagination className="justify-content-center mt-4 flex-wrap">
                <PaginationItem disabled={currentPage===0}>
                    <PaginationLink previous onClick={()=>this.loadPage(currentPage-1)}/>
                </PaginationItem>
                { start>0 && <>
                    <PaginationItem><PaginationLink onClick={()=>this.loadPage(0)}>1</PaginationLink></PaginationItem>
                    <PaginationItem disabled><PaginationLink>…</PaginationLink></PaginationItem>
                </> }
                {pages}
                { end<totalPages-1 && <>
                    <PaginationItem disabled><PaginationLink>…</PaginationLink></PaginationItem>
                    <PaginationItem><PaginationLink onClick={()=>this.loadPage(totalPages-1)}>{totalPages}</PaginationLink></PaginationItem>
                </> }
                <PaginationItem disabled={currentPage===totalPages-1}>
                    <PaginationLink next onClick={()=>this.loadPage(currentPage+1)}/>
                </PaginationItem>
            </Pagination>
        );
    }

    render() {
        if (!sessionStorage.getItem("customer")) return sessionEnded();
        if (this.state.unauthorized) return <Navigate to="/" />;
        const { themes, isLoading, searchQuery, createTitle, creating, createSuccess, createError } = this.state;

        return (
            <>
                <SeoNoIndex noindex={true} />

                <div className="position-relative">
                    {/* Hero Section */}
                    <section className="section section-lg section-shaped pb-250">
                        <div className="shape shape-style-1 shape-default">
                            <span /><span /><span /><span /><span /><span /><span /><span /><span />
                        </div>
                        <Container className="py-lg-md d-flex">
                            <div className="col px-0">
                                <Row>
                                    <Col lg="8">
                                        <h1 className="display-3 text-white">
                                            Building your song-flows<br/><span>manage themes</span>
                                        </h1>
                                    </Col>
                                </Row>
                            </div>
                        </Container>
                        <div className="separator separator-bottom separator-skew">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                preserveAspectRatio="none"
                                viewBox="0 0 2560 100"
                            >
                                <polygon className="fill-white" points="2560 0 2560 100 0 100" />
                            </svg>
                        </div>
                    </section>

                    {/* Main Admin Panel */}
                    <section className="section section-lg pt-lg-0 mt--200">
                        <Container>
                            <Row className="justify-content-center">
                                <Col lg="12">
                                    <Row className="row-grid">
                                        <Col lg="12">
                                            <Card className="shadow border-0">
                                                <Row className="row-grid">
                                                    <CardBody className="py-5">
                                                        {/* Back to menu */}
                                                        <Button color="link" onClick={() => this.props.navigate("/admin")} className="mb-3 p-0">
                                                            &larr; Back to menu
                                                        </Button>

                                                        <h5>Create Theme</h5>

                                                        {createSuccess && (
                                                            <Alert color="success">Theme created successfully</Alert>
                                                        )}

                                                        {createError && (
                                                            <Alert color="danger">{createError}</Alert>
                                                        )}

                                                        <Form onSubmit={(e) => { e.preventDefault(); this.handleCreateSubmit(); }} className="mb-4">
                                                            <InputGroup>
                                                                <Input
                                                                    type="text"
                                                                    placeholder="Theme title"
                                                                    value={createTitle}
                                                                    onChange={this.handleCreateChange}
                                                                />
                                                                <Button
                                                                    color="primary"
                                                                    type="submit"
                                                                    disabled={creating || !createTitle.trim()}
                                                                >
                                                                    {creating ? "Creating..." : "Create"}
                                                                </Button>
                                                            </InputGroup>
                                                        </Form>

                                                        {/* Search Form with Clear button */}
                                                        <Form inline onSubmit={this.handleSearchSubmit} className="mb-3">
                                                            <InputGroup>
                                                                <Input
                                                                    type="text"
                                                                    placeholder="Search themes..."
                                                                    value={searchQuery}
                                                                    onChange={this.handleSearchChange}
                                                                />
                                                                <Button color="primary" type="submit">Search</Button>
                                                                <Button
                                                                    color="secondary"
                                                                    onClick={this.handleClearSearch}
                                                                    disabled={!searchQuery.trim()}
                                                                    className="ml-2"
                                                                >Clear</Button>
                                                            </InputGroup>
                                                        </Form>

                                                        {isLoading ? (
                                                            <div>Loading…</div>
                                                        ) : (
                                                            <ul className="list-group">
                                                                {themes.length>0 ? themes.map(t => (
                                                                    <li
                                                                        key={t.id}
                                                                        className="list-group-item"
                                                                        role="button"
                                                                        style={{cursor: "pointer"}}
                                                                        onClick={() => this.props.navigate(`/admin/theme/${t.id}`)}
                                                                    >
                                                                        <strong>{t.title}</strong>&nbsp;
                                                                        {/*<span className="small text-muted">*/}
                                                                        {/*    {t.words.length} songs*/}
                                                                        {/*</span>*/}
                                                                        <div className="small text-muted">
                                                                            ({t.id})
                                                                        </div>
                                                                    </li>
                                                                )) : (
                                                                    <li className="list-group-item">No themes
                                                                        found.</li>
                                                                )}
                                                            </ul>
                                                        )}

                                                        {this.renderPagination()}
                                                    </CardBody>
                                                </Row>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </Container>
                    </section>
                </div>
            </>
        );
    }
}

export default withNavigateHook(AdminThemes);

// you'll need this helper HOC
function withNavigateHook(Component) {
    return props => {
        const navigate = useNavigate();
        return <Component {...props} navigate={navigate}/>;
    };
}
